import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { DropdownMenuItemProps } from '@radix-ui/react-dropdown-menu';
import { PopperContentProps } from '@radix-ui/react-popper';
import { Fragment, ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { IconDots } from '../../common/icons/cdl/Dots';
import { theme } from '../../common/ui/theme';
import { IconButton } from '../IconButton/IconButton';
import { Text } from '../Text/Text';

import { DropdownMenuContent } from './DropdownMenuContent';
import { DropdownMenuItem } from './DropdownMenuItem';

export interface DropdownMenuOption {
    onSelect: DropdownMenuItemProps['onSelect'];
    label: string;
    description?: string;
    destructive?: boolean;
    disabled?: boolean;
    dividerBefore?: boolean;
}

interface DropdownMenuProps extends Pick<PopperContentProps, 'align'> {
    options: DropdownMenuOption[];
    trigger?: ReactNode;
}

// Theme spacing tokens are stored as px strings, but the width calculation needs raw numbers.
const toPxNumber = (value: string) => Number.parseInt(value, 10);

export const DropdownMenu = ({ trigger = <IconButton Icon={IconDots} />, ...props }: DropdownMenuProps) => {
    const labelRefs = useRef<Array<HTMLSpanElement | null>>([]);
    const [contentWidth, setContentWidth] = useState<number>();

    useLayoutEffect(() => {
        const labelWidth = Math.max(
            ...props.options.map((_, index) => Math.ceil(labelRefs.current[index]?.getBoundingClientRect().width ?? 0)),
            0
        );
        const horizontalPadding = toPxNumber(theme.space[4]) * 2 + toPxNumber(theme.space[8]) * 2;
        const wrappingBuffer = toPxNumber(theme.space[8]);

        // Size the menu from the widest action label only so helper descriptions wrap
        // without stretching the menu wider than the actionable options require.
        setContentWidth(labelWidth > 0 ? labelWidth + horizontalPadding + wrappingBuffer : undefined);
    }, [props.options]);

    return (
        <RadixDropdownMenu.Root modal={false}>
            <RadixDropdownMenu.Trigger asChild onClick={(event) => event.stopPropagation()}>
                {trigger}
            </RadixDropdownMenu.Trigger>

            <RadixDropdownMenu.Portal>
                <DropdownMenuContent sideOffset={5} align={props.align ?? 'start'} $width={contentWidth}>
                    {props.options.map((option, index) => (
                        <Fragment key={index}>
                            {option.dividerBefore ? <RadixDropdownMenu.Separator /> : null}
                            <DropdownMenuItem
                                onClick={(event) => event.stopPropagation()}
                                onSelect={option.onSelect}
                                $destructive={option.destructive}
                                disabled={option.disabled}
                            >
                                <Text variant="fieldLabel" display="block">
                                    {option.label}
                                </Text>
                                {option.description ? (
                                    <Text
                                        variant="extraSmall"
                                        display="block"
                                        color={option.disabled ? 'foreground.disabled' : 'foreground.subtle'}
                                    >
                                        {option.description}
                                    </Text>
                                ) : null}
                            </DropdownMenuItem>
                        </Fragment>
                    ))}
                </DropdownMenuContent>
            </RadixDropdownMenu.Portal>

            <div
                aria-hidden
                style={{
                    position: 'fixed',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    top: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                }}
            >
                {props.options.map((option, index) => (
                    <Text
                        key={`${option.label}-${index}`}
                        variant="fieldLabel"
                        display="block"
                        style={{ whiteSpace: 'nowrap' }}
                        ref={(node) => {
                            labelRefs.current[index] = node;
                        }}
                    >
                        {option.label}
                    </Text>
                ))}
            </div>
        </RadixDropdownMenu.Root>
    );
};
